(ns io.daskul.webapp.widgets.dashboard.ageing-bar
  "Receivables-ageing widget (#729) — a single segmented bar by ₱ amount owed,
   split into days-past-due bands, with a legend and per-band hover (amount +
   invoice count). Amount, not invoice count, drives the widths: collections
   chases money at risk, so one big late invoice should outweigh many small ones.

   Expects {:widget :ageing-bar :label :description
            :segments [{:key :label :amount :count} …] :total <number>}."
  (:require
   [uix.core :refer [defui $]]
   [io.daskul.webapp.ui :as ui]
   [io.daskul.webapp.ui.icons :as icons]))

(def ^:private seg-color
  {:not-yet-due "var(--mantine-color-gray-5)"
   :d1-7        "var(--mantine-color-yellow-6)"
   :d8-14       "var(--mantine-color-orange-5)"
   :d15-21      "var(--mantine-color-orange-7)"
   :d22-40      "var(--mantine-color-red-6)"
   :d41-plus    "var(--mantine-color-red-9)"
   :no-due-date "var(--mantine-color-violet-6)"})

(defn- php [v]
  (.toLocaleString (js/Number. v) "en-PH"
    #js {:style "currency" :currency "PHP" :minimumFractionDigits 0 :maximumFractionDigits 0}))

(defn- band-label
  "Tooltip text for a band, e.g. '1–7 · ₱3,000 · 4 invoices'."
  [label amount count]
  (str label " · " (php amount) " · " count " invoice" (when (not= 1 count) "s")))

(defui ageing-bar
  [{:keys [label description segments total]}]
  (let [visible (filter #(pos? (:amount %)) segments)
        total   (or total (reduce + 0 (map :amount segments)))
        ;; Legend shows the WHOLE ladder (empty bands at ₱0, dimmed) so a zero
        ;; band reads as "nothing here" rather than vanishing — the bar can't
        ;; jump from "Not yet due" to "8–14" with no sign 1–7 exists. The undated
        ;; anomaly band stays hidden unless it actually has money in it.
        legend-bands (filter (fn [{:keys [key amount]}]
                               (or (pos? amount) (not= :no-due-date (keyword key))))
                             segments)]
    ($ ui/paper {:p "lg" :radius "md" :withBorder true}
      ($ ui/group {:gap 6 :align "center" :mb "sm" :wrap "nowrap"}
        ($ ui/text {:size "sm" :fw 700} label)
        (when description
          ($ ui/tooltip {:label description :multiline true :w 260
                         :position "top-end" :withArrow true}
            ($ ui/text {:c "dimmed" :style #js {:cursor "help"} :lh 1}
              ($ icons/icon-info-circle {:size 14 :stroke 1.5})))))
      (if (or (empty? visible) (not (pos? total)))
        ($ ui/text {:size "sm" :c "dimmed"} "Nothing outstanding.")
        ($ :<>
          ;; segmented bar — each band a Mantine-tooltipped slice (:key arrives
          ;; from JSON as a string; seg-color is keyword-keyed, so coerce).
          ($ ui/box {:style {:display "flex" :height 12 :borderRadius 6
                             :overflow "hidden" :marginBottom 12}}
            (for [{:keys [key label amount count]} visible]
              (let [k (keyword key)]
                ($ ui/tooltip {:key (name k) :label (band-label label amount count)
                               :withArrow true :position "top"}
                  ($ ui/box {:style {:width (str (* 100 (/ amount total)) "%")
                                     :cursor "help"
                                     :background (seg-color k)}})))))
          ;; legend — the whole band ladder; empty bands dimmed; each tooltipped
          ;; with its amount + invoice count.
          ($ ui/group {:gap "md" :wrap "wrap"}
            (for [{:keys [key label amount count]} legend-bands]
              (let [k (keyword key)]
                ($ ui/tooltip {:key (name k) :label (band-label label amount count)
                               :withArrow true :position "top"}
                  ($ ui/group {:gap 6 :align "center" :wrap "nowrap"
                               :style {:opacity (if (pos? amount) 1 0.45) :cursor "help"}}
                    ($ ui/box {:style {:width 9 :height 9 :borderRadius 3
                                       :background (seg-color k)}})
                    ($ ui/text {:size "xs" :c "dimmed"} label)
                    ($ ui/text {:size "xs" :fw 600
                                :style {:fontFamily "Sora, sans-serif" :fontVariantNumeric "tabular-nums"}}
                      (php amount))))))))))))
